import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useAppSelector, useQueryParamValue } from 'src/utils/Hooks';
import {
  getComparisonWeekId,
  getFirstWeekIdOfYear,
  getLastWeekIdOfYear,
  getNextWeekId,
  getPreviousWeekId,
  getPreviousYearWeekId,
  getWeekId,
  yearPartOfWeekId
} from 'src/utils/dateUtils';
import { AdjustmentPlanType, ForecastComparisonPeriod, ForecastPeriod, ForecastType } from '../types';
import axios from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import _get from 'lodash/get';

/**
 * Fetches the latest forecast model for the client + retailer combination.
 * @returns an object contain the modelVersion and a loading state
 */
export const useLatestForecastModel = (): {
  modelVersion: string | null;
  loading: boolean;
  currentPublishVersion: string;
  previousPublishVersion: string;
} => {
  const cancelSource = useRef(axios.CancelToken.source());
  const beaconClientId = useAppSelector((state) => state.user.config.vendor.BeaconClientId);
  const retailerId = useAppSelector((state) => state.retailer.id);

  const query = useQuery({
    queryKey: ['forecastModelRequest', beaconClientId, retailerId],
    queryFn: async () => {
      try {
        const response = await axios.get(
          `api/beaconforecast/GetModelVersion?beaconClientId=${beaconClientId}&retailerId=${retailerId}`
        );
        return response;
      } catch (error) {
        console.error('Failed to fetch forecasting model');
        return null;
      }
    },
    retry: 0, // Axios configured for 3 retries already
    staleTime: 5 * 60 * 1000, // 5 minutes - model version rarely changes
    refetchOnWindowFocus: false
  });

  useEffect(() => {
    const cancelNetworkCalls = cancelSource.current.cancel;

    return () => cancelNetworkCalls();
  }, []);

  const { data, isLoading } = query;

  // Memoize the return value to prevent unnecessary re-renders
  return useMemo(() => {
    const modelVersion = _get(data, ['data', 'modelVersion'], null);

    // TODO: Remove this once we support all clients and retailers. Use fallback model for Weleda
    if (!isLoading && !modelVersion) {
      return {
        modelVersion: '202323',
        loading: isLoading,
        currentPublishVersion: '202323_01',
        previousPublishVersion: '202323_01'
      };
    }

    return {
      modelVersion,
      loading: isLoading,
      currentPublishVersion: _get(data, ['data', 'currentPublishVersion'], null),
      previousPublishVersion: _get(data, ['data', 'previousPublishVersion'], null)
    };
  }, [isLoading, data]);
};

/**
 * Given a week ID, get the previous week ID to compare it to.
 * If the comparison period is the prior year, then it will return
 * the week ID one year prior. If the comparison period is the prior
 * period, it will return the week ID `n` weeks back, where `n` is the
 * number of weeks in the forecast period.
 */
export const usePreviousForecastWeekId = () => {
  const forecastPeriod: ForecastPeriod = useQueryParamValue('forecastPeriod', ForecastPeriod.FULL_YEAR);
  const comparisonPeriod: ForecastComparisonPeriod = useQueryParamValue('pid', ForecastComparisonPeriod.PRIOR_YEAR);

  return useCallback(
    (weekId: number) => {
      if (comparisonPeriod === ForecastComparisonPeriod.PRIOR_YEAR || forecastPeriod === ForecastPeriod.FULL_YEAR) {
        return getPreviousYearWeekId(weekId);
      } else {
        const weeksToSubtract: number = {
          [ForecastPeriod.FIFTY_TWO_WEEKS]: 52,
          [ForecastPeriod.TWENTY_SIX_WEEKS]: 26,
          [ForecastPeriod.THIRTEEN_WEEKS]: 13,
          [ForecastPeriod.FOUR_WEEKS]: 4
        }[forecastPeriod];
        return getPreviousWeekId(weekId, weeksToSubtract);
      }
    },
    [forecastPeriod, comparisonPeriod]
  );
};

/**
 * Gets the start and end week IDs for the custom forecast period
 * @param forecastPeriod
 * @returns The new start or end week ID
 */
export const getCustomForecastDateWeek = (startOrEnd: 'start' | 'end') => {
  const searchParams = new URLSearchParams(window.location.search);
  const forecastPeriodQueryParam = searchParams.get('forecastPeriod');
  const [startWeekId, endWeekId] = forecastPeriodQueryParam.split('-');
  if (startOrEnd === 'start') {
    return startWeekId;
  }
  return endWeekId;
};

export const customForecastDateRegex = /^\d{6}-\d{6}$/;
export const useForecastPeriod = (): ForecastPeriod => {
  const forecastPeriod = useQueryParamValue('forecastPeriod', ForecastPeriod.FULL_YEAR);
  // Regex: 6 digits followed by a hyphen and then 6 more digits
  // If the forecast period matches the custom format, return custom enum
  if (customForecastDateRegex.test(forecastPeriod)) {
    return ForecastPeriod.CUSTOM;
  }
  return forecastPeriod;
};

export const useForecastComparisonPeriod = (): ForecastComparisonPeriod => {
  const forecastComparisonPeriod = useQueryParamValue('pid', ForecastComparisonPeriod.PRIOR_YEAR);
  return forecastComparisonPeriod;
};

export const useForecastType = (): ForecastType => {
  const forecastType = useQueryParamValue('forecastType', ForecastType.ADJUSTED);
  return forecastType;
};

export const useForecastPlanType = (): AdjustmentPlanType => {
  const forecastPlanType = useQueryParamValue('forecastPlansType', AdjustmentPlanType.AllPlanTypes);
  return forecastPlanType;
};

/**
 * Given the forecast period in the URL, calculate the start
 * and end week IDs. For example, if the next 13 weeks are chosen,
 * it will return the end week ID of the 13 weeks from the current
 * week id.
 */
export const useForecastStartAndEndWeekIds = ({
  forecastPeriodOverride
}: { forecastPeriodOverride?: ForecastPeriod } = {}) => {
  const { modelVersion } = useLatestForecastModel();
  // Start the forecast the week after the current week
  // Use current week as fallback when modelVersion is null (still loading)
  const baseWeekId = useMemo(() => {
    return modelVersion ? Number(modelVersion) : getWeekId();
  }, [modelVersion]);

  const startWeekId = useMemo(() => {
    return getNextWeekId(baseWeekId);
  }, [baseWeekId]);

  const forecastPeriod = useForecastPeriod();
  const customStartWeek = forecastPeriod === ForecastPeriod.CUSTOM ? getCustomForecastDateWeek('start') : null;
  const customEndWeek = forecastPeriod === ForecastPeriod.CUSTOM ? getCustomForecastDateWeek('end') : null;

  // Get the last week ID, then we will use the next
  // one to get the forecast data for the rest of the year
  // For 52W and Full Year: use startWeekId (modelVersion + 1)
  // For shorter periods (26W, 13W, 4W): use baseWeekId (modelVersion) directly
  const endWeekId: number = useMemo(() => {
    const result =
      {
        [ForecastPeriod.FULL_YEAR]: getLastWeekIdOfYear(yearPartOfWeekId(startWeekId)),
        [ForecastPeriod.FIFTY_TWO_WEEKS]: getNextWeekId(startWeekId, 52),
        [ForecastPeriod.TWENTY_SIX_WEEKS]: getNextWeekId(baseWeekId, 26),
        [ForecastPeriod.THIRTEEN_WEEKS]: getNextWeekId(baseWeekId, 13),
        [ForecastPeriod.FOUR_WEEKS]: getNextWeekId(baseWeekId, 4)
      }[forecastPeriodOverride || forecastPeriod] || customEndWeek;
    return result;
  }, [forecastPeriod, forecastPeriodOverride, startWeekId, baseWeekId, customEndWeek]);

  return useMemo(() => {
    const result = { startWeekId: +customStartWeek || startWeekId, endWeekId };
    return result;
  }, [endWeekId, startWeekId, customStartWeek]);
};

/**
 * Given the forecast comparison period in the URL, calculate the start
 * and end week IDs.
 * Uses the API-based comparison week lookup for accurate YoY mapping.
 */
export const useForecastComparisonStartAndEndWeekIds = ({
  startWeekIdOverride,
  endWeekIdOverride
}: {
  startWeekIdOverride?: number;
  endWeekIdOverride?: number;
} = {}) => {
  const forecastPeriod = useQueryParamValue('forecastPeriod', ForecastPeriod.FULL_YEAR);
  const comparisonPeriod: ForecastComparisonPeriod = useQueryParamValue('pid', ForecastComparisonPeriod.PRIOR_YEAR);
  const { startWeekId: forecastStartWeekId, endWeekId: forecastEndWeekId } = useForecastStartAndEndWeekIds();
  const { modelVersion } = useLatestForecastModel();
  const lastPublishedWeekId = modelVersion ? Number(modelVersion) : 0;

  const baseStartWeekId = startWeekIdOverride ?? forecastStartWeekId;
  const baseEndWeekId = endWeekIdOverride ?? forecastEndWeekId;
  const hasCustomRangeOverride = startWeekIdOverride != null || endWeekIdOverride != null;

  // For Prior Year we always hit the time API with the forecast range to get comparison start/end.
  // For FULL_YEAR the "forecast range" for comparison is the full current year; otherwise use forecast start/end.
  const currentYear = yearPartOfWeekId(baseStartWeekId);
  const firstWeekOfYear = getFirstWeekIdOfYear(currentYear);
  const lastWeekOfYear = getLastWeekIdOfYear(currentYear);
  const startWeekForApi =
    forecastPeriod === ForecastPeriod.FULL_YEAR && !hasCustomRangeOverride ? firstWeekOfYear : baseStartWeekId;
  const endWeekForApi =
    forecastPeriod === ForecastPeriod.FULL_YEAR && !hasCustomRangeOverride ? lastWeekOfYear : baseEndWeekId;

  // For prior year comparison, fetch comparison weeks from API
  const shouldUsePriorYearApi =
    comparisonPeriod === ForecastComparisonPeriod.PRIOR_YEAR || forecastPeriod === ForecastPeriod.FULL_YEAR;

  // Memoize placeholder data to avoid recalculating on every render
  const startPlaceholder = useMemo(
    () => (startWeekForApi > 0 ? getPreviousYearWeekId(startWeekForApi) : undefined),
    [startWeekForApi]
  );
  const endPlaceholder = useMemo(
    () => (endWeekForApi > 0 ? getPreviousYearWeekId(endWeekForApi) : undefined),
    [endWeekForApi]
  );

  // Fetch comparison week for start - use placeholderData for instant display
  const { data: comparisonStartFromApi, isLoading: isLoadingStart } = useQuery({
    queryKey: ['forecastComparisonWeek', 'start', startWeekForApi],
    queryFn: () => getComparisonWeekId(startWeekForApi),
    enabled: shouldUsePriorYearApi && startWeekForApi > 0,
    staleTime: 60 * 60 * 1000, // 1 hour - same as the library cache
    refetchOnWindowFocus: false,
    // Use memoized local calculation as placeholder to prevent UI lag
    placeholderData: startPlaceholder
  });

  // Fetch comparison week for end - use placeholderData for instant display
  const { data: comparisonEndFromApi, isLoading: isLoadingEnd } = useQuery({
    queryKey: ['forecastComparisonWeek', 'end', endWeekForApi],
    queryFn: () => getComparisonWeekId(endWeekForApi),
    enabled: shouldUsePriorYearApi && endWeekForApi > 0,
    staleTime: 60 * 60 * 1000, // 1 hour - same as the library cache
    refetchOnWindowFocus: false,
    // Use memoized local calculation as placeholder to prevent UI lag
    placeholderData: endPlaceholder
  });

  // Fallback function for prior period calculation
  // Shifts the week back by the forecast period length
  const getPriorPeriodWeekId = useCallback(
    (weekId: number) => {
      if (!weekId || weekId <= 0) return 0;

      const weeksToSubtract: number =
        {
          [ForecastPeriod.FULL_YEAR]: 52, // Full year uses 52 weeks for prior period
          [ForecastPeriod.FIFTY_TWO_WEEKS]: 52,
          [ForecastPeriod.TWENTY_SIX_WEEKS]: 26,
          [ForecastPeriod.THIRTEEN_WEEKS]: 13,
          [ForecastPeriod.FOUR_WEEKS]: 4
        }[forecastPeriod] || 52;
      return getPreviousWeekId(weekId, weeksToSubtract);
    },
    [forecastPeriod]
  );

  const isLoading = isLoadingStart || isLoadingEnd;

  return useMemo(() => {
    let startWeekId: number;
    let endWeekId: number;

    if (forecastPeriod === ForecastPeriod.FULL_YEAR) {
      // For FULL_YEAR, comparison spans the entire prior year — both start and end from time API (full year range)
      startWeekId = comparisonStartFromApi ?? getPreviousYearWeekId(startWeekForApi);
      endWeekId = comparisonEndFromApi ?? getPreviousYearWeekId(endWeekForApi);
    } else if (comparisonPeriod === ForecastComparisonPeriod.PRIOR_YEAR) {
      // For PRIOR_YEAR comparison (non-full year), use API-based week IDs with fallback
      startWeekId = comparisonStartFromApi ?? getPreviousYearWeekId(startWeekForApi);
      endWeekId = comparisonEndFromApi ?? getPreviousYearWeekId(endWeekForApi);
    } else {
      // For PRIOR_PERIOD comparison, shift back by the forecast period length.
      // Cap at last published week so we don't request/align to weeks without data.
      startWeekId = getPriorPeriodWeekId(baseStartWeekId);
      const rawEndWeekId = getPriorPeriodWeekId(baseEndWeekId);
      endWeekId = lastPublishedWeekId > 0 ? Math.min(rawEndWeekId, lastPublishedWeekId) : rawEndWeekId;
    }

    return { startWeekId, endWeekId, isLoading };
  }, [
    forecastPeriod,
    comparisonPeriod,
    comparisonStartFromApi,
    comparisonEndFromApi,
    getPriorPeriodWeekId,
    baseStartWeekId,
    baseEndWeekId,
    startWeekForApi,
    endWeekForApi,
    isLoading,
    lastPublishedWeekId
  ]);
};

/**
 * With forecasted data in the YTD view, we want to show the actual
 * data for YTD along with the forecasted data. This returns the date
 * range for YTD.
 */
export const useYtdStartAndEndWeekIdsForForecast = () => {
  const { startWeekId: forecastStartWeekId } = useForecastStartAndEndWeekIds();
  let endWeekId = getPreviousWeekId(forecastStartWeekId);

  if (endWeekId % 100 === 52) {
    endWeekId = forecastStartWeekId;
  }

  return {
    startWeekId: getFirstWeekIdOfYear(yearPartOfWeekId(endWeekId)),
    endWeekId
  };
};

/**
 * Exports forecasts needs this weekIds, for historic data, and forecasts data
 */
export const useExportStartAndEndWeekIds = () => {
  const forecastPeriod = useForecastPeriod();
  const { modelVersion } = useLatestForecastModel();
  const { startWeekId: forecastStartWeekId, endWeekId: forecastEndWeekId } = useForecastStartAndEndWeekIds();
  const { startWeekId: historicStartWeekId, endWeekId: historicEndWeekId } = useForecastComparisonStartAndEndWeekIds();
  // (Assume today is 202410)  if full, historic data request range should start from year 202401. not 202301 like normal forecastComparisonStartAndWeekIds()
  if (forecastPeriod === ForecastPeriod.FULL_YEAR) {
    return {
      historicStartWeekId: getFirstWeekIdOfYear(yearPartOfWeekId(Number(forecastStartWeekId))),
      historicEndWeekId: Number(modelVersion),
      forecastStartWeekId,
      forecastEndWeekId
    };
  }
  return {
    historicStartWeekId,
    historicEndWeekId,
    forecastStartWeekId,
    forecastEndWeekId
  };
};

/**
 * Hook to continuously invalidate queries on a given interval.
 * If the query is observed, it will be invalidated and refetched automatically.
 */
export const useInvalidateQueriesOnInterval = ({ queryKeys, interval }: { queryKeys: any[]; interval: number }) => {
  const queryClient = useQueryClient();

  useEffect(() => {
    const intervalId = setInterval(() => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    }, interval);

    return () => clearInterval(intervalId);
  }, [queryClient, queryKeys, interval]);
};
